import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, FileSearch, Edit, Trash2, CheckCircle, ChevronLeft, ChevronRight, X, Star } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_MAP = {
  aperta:         { label: 'Aperta',         color: 'bg-blue-50 text-blue-700 border-blue-200' },
  in_valutazione: { label: 'In Valutazione', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  aggiudicata:    { label: 'Aggiudicata',    color: 'bg-green-50 text-green-700 border-green-200' },
  chiusa:         { label: 'Chiusa',         color: 'bg-slate-100 text-slate-700 border-slate-300' },
  annullata:      { label: 'Annullata',      color: 'bg-red-50 text-red-700 border-red-200' },
};

const emptyForm = {
  date: new Date().toISOString().split('T')[0], deadline: '',
  subject: '', supplier_ids: [], site_id: '', notes: '', items: [],
};

export default function RDOPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse', 'capo_cantiere');
  const [rdos, setRdos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [selectedRdo, setSelectedRdo] = useState(null);
  const [offerForm, setOfferForm] = useState({ supplier_id: '', supplier_name: '', amount: 0, delivery_days: 0, notes: '', received_date: new Date().toISOString().split('T')[0] });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== '__all__') params.status = filterStatus;
      const [rRes, sRes, siRes] = await Promise.all([
        api.get('/rdo', { params }),
        api.get('/suppliers/names'),
        api.get('/sites'),
      ]);
      setRdos(rRes.data);
      setSuppliers(sRes.data);
      setSites(siRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); setPage(1); }, [load]);

  const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm, items: [], supplier_ids: [] }); setShowDialog(true); };
  const openEdit = (r) => {
    setEditItem(r);
    setForm({ date: r.date, deadline: r.deadline || '', subject: r.subject || '', supplier_ids: r.supplier_ids || [], site_id: r.site_id || '', notes: r.notes || '', items: (r.items || []).map(i => ({ ...i })) });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) { await api.put(`/rdo/${editItem.id}`, form); }
      else { await api.post('/rdo', form); }
      setShowDialog(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa RDO?')) return;
    try { await api.delete(`/rdo/${id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openOfferDialog = (rdo) => {
    setSelectedRdo(rdo);
    setOfferForm({ supplier_id: '', supplier_name: '', amount: 0, delivery_days: 0, notes: '', received_date: new Date().toISOString().split('T')[0] });
    setShowOfferDialog(true);
  };

  const handleAddOffer = async () => {
    try {
      const sup = suppliers.find(s => s.id === offerForm.supplier_id);
      await api.post(`/rdo/${selectedRdo.id}/offers`, { ...offerForm, supplier_name: sup?.name || '' });
      setShowOfferDialog(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleSelectOffer = async (rdoId, offerId) => {
    try { await api.post(`/rdo/${rdoId}/offers/${offerId}/select`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, unit: 'pz', notes: '' }] }));
  const updateItem = (idx, field, val) => setForm(f => { const l = [...f.items]; l[idx] = { ...l[idx], [field]: val }; return { ...f, items: l }; });
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const toggleSupplier = (id) => {
    setForm(f => {
      const ids = f.supplier_ids.includes(id) ? f.supplier_ids.filter(x => x !== id) : [...f.supplier_ids, id];
      return { ...f, supplier_ids: ids };
    });
  };

  const paged = rdos.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(rdos.length / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="rdo-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">RDO - Richieste di Offerta</h1>
          <p className="text-sm text-slate-500 mt-1">Richieste verso fornitori con confronto offerte</p>
        </div>
        {canManage && <Button onClick={openCreate} data-testid="add-rdo-btn"><Plus size={16} className="mr-1" /> Nuova RDO</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(STATUS_MAP).map(([key, cfg]) => (
          <Card key={key} className="border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center"><FileSearch size={18} className="text-slate-600" /></div>
              <div><p className="text-xs text-slate-500">{cfg.label}</p><p className="text-xl font-bold text-slate-700">{rdos.filter(r => r.status === key).length}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Select value={filterStatus} onValueChange={setFilterStatus}>
        <SelectTrigger className="w-48" data-testid="rdo-status-filter"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tutti gli stati</SelectItem>
          {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>N. RDO</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Oggetto</TableHead>
                <TableHead>Fornitori</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Offerte</TableHead>
                <TableHead>Stato</TableHead>
                {canManage && <TableHead className="w-32">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Nessuna RDO</TableCell></TableRow>
              ) : paged.map(r => (
                <TableRow key={r.id} className="hover:bg-slate-50/50" data-testid={`rdo-row-${r.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{r.rdo_number}</TableCell>
                  <TableCell className="text-sm">{r.date}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{r.subject || '-'}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex flex-wrap gap-1">{(r.supplier_names || []).map((n, i) => <Badge key={i} variant="outline" className="text-[10px]">{n}</Badge>)}</div>
                  </TableCell>
                  <TableCell className="text-sm">{r.deadline || '-'}</TableCell>
                  <TableCell className="text-sm text-center">
                    {(r.offers || []).length}
                    {(r.offers || []).some(o => o.selected) && <Star size={12} className="inline ml-1 text-amber-500 fill-amber-500" />}
                  </TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_MAP[r.status]?.color || ''}`}>{STATUS_MAP[r.status]?.label || r.status}</Badge></TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(r)}><Edit size={13} /></Button>
                        {(r.status === 'aperta' || r.status === 'in_valutazione') && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => openOfferDialog(r)} title="Aggiungi Offerta"><Plus size={13} /></Button>
                        )}
                        {isRole('admin') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(r.id)}><Trash2 size={13} /></Button>}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-slate-500">{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, rdos.length)} di {rdos.length}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
            <span className="text-xs text-slate-600 px-2">Pag. {page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* RDO detail - offers for selected rdo */}
      {rdos.filter(r => (r.offers || []).length > 0).length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm text-slate-900 mb-3">Confronto Offerte Ricevute</h3>
            {rdos.filter(r => (r.offers || []).length > 0).slice(0, 5).map(r => (
              <div key={r.id} className="mb-4 border rounded-lg p-3">
                <p className="text-sm font-medium mb-2">{r.rdo_number} - {r.subject}</p>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="text-xs">Fornitore</TableHead>
                      <TableHead className="text-xs text-right">Importo</TableHead>
                      <TableHead className="text-xs text-center">GG Consegna</TableHead>
                      <TableHead className="text-xs">Note</TableHead>
                      <TableHead className="text-xs w-20">Sel.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {r.offers.map(o => (
                      <TableRow key={o.offer_id} className={o.selected ? 'bg-green-50/50' : ''}>
                        <TableCell className="text-sm">{o.supplier_name || '-'}</TableCell>
                        <TableCell className="text-sm text-right font-medium">{fmt(o.amount)}</TableCell>
                        <TableCell className="text-sm text-center">{o.delivery_days}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{o.notes || '-'}</TableCell>
                        <TableCell>
                          {o.selected ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]">Scelta</Badge>
                          ) : canManage && r.status !== 'chiusa' ? (
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-green-700" onClick={() => handleSelectOffer(r.id, o.offer_id)}>
                              <CheckCircle size={12} className="mr-0.5" /> Scegli
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader><DialogTitle>{editItem ? 'Modifica RDO' : 'Nuova RDO'}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="rdo-date-input" /></div>
              <div className="space-y-1.5"><Label>Scadenza</Label><Input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} /></div>
              <div className="col-span-2 space-y-1.5"><Label>Oggetto *</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="rdo-subject-input" /></div>
              <div className="col-span-2 space-y-1.5">
                <Label>Fornitori Destinatari</Label>
                <div className="flex flex-wrap gap-2 border rounded-lg p-2 max-h-32 overflow-y-auto">
                  {suppliers.map(s => (
                    <Badge key={s.id} variant={form.supplier_ids.includes(s.id) ? 'default' : 'outline'}
                      className={`cursor-pointer text-xs ${form.supplier_ids.includes(s.id) ? 'bg-blue-600 text-white' : ''}`}
                      onClick={() => toggleSupplier(s.id)} data-testid={`rdo-sup-${s.id}`}>
                      {s.name}
                    </Badge>
                  ))}
                  {suppliers.length === 0 && <p className="text-xs text-slate-400">Nessun fornitore. Creane uno prima.</p>}
                </div>
              </div>
              <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            </div>
            <div className="mt-4 border rounded-lg p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Articoli Richiesti</Label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="rdo-add-item"><Plus size={12} className="mr-1" /> Riga</Button>
              </div>
              {form.items.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">Nessun articolo</p>
              ) : form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-5"><Input placeholder="Descrizione" value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input placeholder="U.M." value={it.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input placeholder="Note" value={it.notes || ''} onChange={e => updateItem(idx, 'notes', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-1"><Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeItem(idx)}><X size={14} /></Button></div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.subject.trim()} data-testid="rdo-save-btn">{editItem ? 'Salva' : 'Crea RDO'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aggiungi Offerta - {selectedRdo?.rdo_number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Fornitore *</Label>
              <Select value={offerForm.supplier_id} onValueChange={v => setOfferForm({ ...offerForm, supplier_id: v })}>
                <SelectTrigger data-testid="offer-supplier-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Importo *</Label><Input type="number" step="0.01" value={offerForm.amount} onChange={e => setOfferForm({ ...offerForm, amount: e.target.value })} data-testid="offer-amount-input" /></div>
            <div className="space-y-1.5"><Label>GG Consegna</Label><Input type="number" value={offerForm.delivery_days} onChange={e => setOfferForm({ ...offerForm, delivery_days: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Ricezione</Label><Input type="date" value={offerForm.received_date} onChange={e => setOfferForm({ ...offerForm, received_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={offerForm.notes} onChange={e => setOfferForm({ ...offerForm, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferDialog(false)}>Annulla</Button>
            <Button onClick={handleAddOffer} disabled={!offerForm.supplier_id} data-testid="offer-save-btn">Aggiungi Offerta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
